// TODO: Finish working on the header and see what code needs to be uncommented
import { useCallback, useEffect, useMemo } from "react";
import { Cookies } from "react-cookie";
import {
  PathMatch,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";
// import { GlobalHeader as GlobalHeaderBase } from "@ubnt/store.core/lib/components/Header/Header";
import { MicrositeHeader } from "@ubnt/store.core/lib/components/MicrositeHeader";

import { COOKIE_MICROSITE_LANGUAGE } from "../../context/SiteSettingsContext/context";
import { useSiteSettings } from "../../context/SiteSettingsContext/context";
// import { useAuth } from "../../context/AuthContext";

import { refreshOneTrustGtag, startOneTrustGtagSync } from "util/oneTrust";

import {
  BASE_PATH,
  getPageBasePaths,
  LANGUAGE_PARAM,
  saveCookie,
  STORE_ID_PARAM,
} from "context/SiteSettingsContext/utils";
import { LocalStorageKey } from "context/SiteSettingsContext";
import { HeaderWrap } from "./Header.styles";
import { useAuth } from "context/AuthContext";

// import { AccountMenu } from "@ubnt/store.core/lib/components/Header/AccountMenu";

/**
 * Pathnames excluding root "/" path
 * For that case change the default below
 */
const LIGHT_MOTIF_PAGE_PATHNAMES = [
  "/download",
  "/how-it-works",
  "/whats-new",
  "/site-support",
  "/cloud-gateways/resource-calculator",
  "/partner-hub",
  "/integrations/managed-voip/international-rates",
];

export const GlobalHeader = function GlobalHeader() {
  const location = useLocation();
  const navigate = useNavigate();

  const [path, regionPath, regionLanguagePath] = getPageBasePaths("/");

  // v6 replacement for useRouteMatch; we want an "exact" match check
  const matchPath = useMatch(path || BASE_PATH);
  const matchRegionPath = useMatch(regionPath || BASE_PATH);
  const matchRegionLanguagePath = useMatch(regionLanguagePath || BASE_PATH);

  // Additional matches for exact paths without trailing slashes
  const matchExactRoot = useMatch("/");
  const matchExactStore = useMatch(`/${STORE_ID_PARAM}`);
  const matchExactStoreLang = useMatch(`/${STORE_ID_PARAM}/${LANGUAGE_PARAM}`);
  const normalizePath = (path: string) => path.replace(/\/$/, "") || "/";

  const { store, language, onLinkClick, canRunUsAutoAnalyticsConsent } =
    useSiteSettings();
  const {
    user,
    loading: loadingAuth,
    handleLogin,
    handleLogout,
    handleRegister,
    handleManageAccount,
  } = useAuth();

  /** @type {import("@ubnt/store.core/lib/components/Header/Header.types").HeaderAccount} */
  const account = useMemo(() => {
    return {
      loading: loadingAuth,
      customer: user,
      handleLogin,
      handleLogout,
      handleRegister,
      handleManageAccount,
    };
  }, [
    handleLogin,
    handleLogout,
    handleManageAccount,
    handleRegister,
    loadingAuth,
    user,
  ]);

  // #############################################################################
  // Motif
  // #############################################################################
  const motif = useMemo(() => {
    const normalizedLocationPath = normalizePath(location.pathname);

    const isExact = (possibleMatch: PathMatch | null) => {
      if (!possibleMatch?.pathname) {
        return false;
      }

      const normalizedMatchPath = normalizePath(possibleMatch.pathname);
      return normalizedMatchPath === normalizedLocationPath;
    };

    const possibleHomepageMatches = [
      isExact(matchPath),
      isExact(matchRegionPath),
      isExact(matchRegionLanguagePath),
      isExact(matchExactRoot),
      isExact(matchExactStore),
      isExact(matchExactStoreLang),
    ];

    const isHomepage = possibleHomepageMatches.some(Boolean);

    const isLightThemePage = LIGHT_MOTIF_PAGE_PATHNAMES.some((p) =>
      location.pathname.includes(p),
    );

    return isHomepage || isLightThemePage ? "light" : "dark";
  }, [
    location.pathname,
    matchPath,
    matchRegionLanguagePath,
    matchRegionPath,
    matchExactRoot,
    matchExactStore,
    matchExactStoreLang,
  ]);

  // #############################################################################
  // Custom menu item action
  // #############################################################################
  const onMenuItemLinkClick = useCallback(
    (e, url) => {
      onLinkClick(e, url);
    },
    [onLinkClick],
  );

  useEffect(() => {
    if (!canRunUsAutoAnalyticsConsent) {
      return;
    }
    return startOneTrustGtagSync(store.id, language);
  }, [store.id, language, canRunUsAutoAnalyticsConsent]);

  const handleCookie = useCallback((language: string) => {
    const cookies = new Cookies();
    const languageCookie = cookies.get(COOKIE_MICROSITE_LANGUAGE);

    if (languageCookie) {
      cookies.remove(COOKIE_MICROSITE_LANGUAGE);
    }

    try {
      saveCookie(COOKIE_MICROSITE_LANGUAGE, language.toUpperCase());
      localStorage.removeItem(LocalStorageKey.LanguageReferer);
    } catch (e) {
      console.log("saving microsite cookie error: ", e);
    }
  }, []);

  const onLanguageChange = useCallback(
    (e, pathname: string, language: string) => {
      e.preventDefault();
      handleCookie(language);
      navigate(pathname);

      if (canRunUsAutoAnalyticsConsent) {
        refreshOneTrustGtag(store.id, language);
      }
    },
    [canRunUsAutoAnalyticsConsent, handleCookie, navigate, store.id],
  );

  return (
    <HeaderWrap>
      <MicrositeHeader
        onMenuItemClick={onMenuItemLinkClick}
        onLanguageChange={onLanguageChange}
        motif={motif}
        account={account}
      />
    </HeaderWrap>
  );
};
